import React, { useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Badge from '@cloudscape-design/components/badge';
import TextFilter from '@cloudscape-design/components/text-filter';
import Popover from '@cloudscape-design/components/popover';
import { MetricsResponse, MissingMetric } from '../apis/metricsApi';

interface Props {
  data: MetricsResponse;
  onFeedback: (metricName: string, helpful: string) => void;
}

export const MetricsDashboard: React.FC<Props> = ({ data, onFeedback }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');

  const metrics = data.metrics.filter(m => {
    if (search && !JSON.stringify(m).toLowerCase().includes(search.toLowerCase())) return false;
    // AUTO-ROLLBACK-EXISTING = only in auto-rollback tab (just needs alarm, no new code)
    const isExistingOnly = m.priority === 'AUTO-ROLLBACK-EXISTING';
    // Main filter: by priority/section (supports both old P0/P1/P2 and new recommended/nice-to-have)
    if (filter === 'recommended' && !((m.priority === 'recommended' || m.priority === 'P0' || m.priority === 'P1') && !isExistingOnly)) return false;
    if (filter === 'nice-to-have' && !(m.priority === 'nice-to-have' || m.priority === 'P2')) return false;
    if (filter === 'auto-rollback' && !(m.section === 'auto-rollback' || m.tag === 'AUTO-ROLLBACK' || m.priority === 'AUTO-ROLLBACK-EXISTING')) return false;
    // Sub filter: by tag (dynamic — matches exact tag value from data)
    if (subFilter !== 'all' && filter === 'recommended') {
      if (m.tag !== subFilter) return false;
    }
    // Section filter: service vs dependency
    if (sectionFilter === 'service' && m.section !== 'service') return false;
    if (sectionFilter === 'dependency' && m.section !== 'dependency') return false;
    return true;
  });

  // Helper: is this metric ONLY for auto-rollback tab (existing metric, just needs alarm)
  const isExistingOnly = (m: any) => m.priority === 'AUTO-ROLLBACK-EXISTING';

  const counts = {
    recommended: data.metrics.filter(m => (m.priority === 'recommended' || m.priority === 'P0' || m.priority === 'P1') && !isExistingOnly(m)).length,
    niceToHave: data.metrics.filter(m => m.priority === 'nice-to-have' || m.priority === 'P2').length,
    autoRollback: data.metrics.filter(m => m.section === 'auto-rollback' || m.tag === 'AUTO-ROLLBACK' || m.priority === 'AUTO-ROLLBACK-EXISTING').length,
    total: data.metrics.length,
  };

  // Dynamic tag discovery — get all unique tags from recommended metrics
  const recommendedMetrics = data.metrics.filter(m => (m.priority === 'recommended' || m.priority === 'P0' || m.priority === 'P1') && !isExistingOnly(m));
  const tagMap: Record<string, { count: number; label: string }> = {};
  const tagLabels: Record<string, string> = {
    // Core tags
    'silent-failure': '🚨 Undetectable Failures',
    'data-quality': '📊 Wrong Decisions',
    'business-visibility': '👁️ Blind Spots',
    'deployment-safety': '🔄 Auto-Rollback',
    'compliance-risk': '🔒 Compliance Risk',
    'capacity-waste': '📦 Capacity Waste',
    'labor-credit-lost': '👷 Labor Credit Lost',
    'missed-optimization': '⚡ Missed Optimization',
    // Old format tags (backward compat)
    'SAFETY': '🚨 Undetectable Failures',
    'AVAILABILITY': '📊 Wrong Decisions',
    'BUSINESS': '👁️ Blind Spots',
    'AUTO-ROLLBACK': '🔄 Auto-Rollback',
  };
  recommendedMetrics.forEach(m => {
    const tag = m.tag || 'other';
    if (!tagMap[tag]) {
      tagMap[tag] = { count: 0, label: tagLabels[tag] || `🏷️ ${tag}` };
    }
    tagMap[tag].count++;
  });

  // Section counts (service vs dependency)
  const sectionCounts = {
    service: data.metrics.filter(m => m.section === 'service').length,
    dependency: data.metrics.filter(m => m.section === 'dependency').length,
  };

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Analysis summary</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <StatCard value={counts.recommended} label="Recommended" type="warning" />
          <StatCard value={counts.niceToHave} label="Nice-to-have" type="info" />
          <StatCard value={counts.autoRollback} label="Auto-Rollback Gaps" type="error" />
          <StatCard value={counts.total} label="Total metrics" type="pending" />
        </ColumnLayout>
      </Container>

      <Container header={
        <Header variant="h2" info={
          <Popover
            header="Understanding the categories"
            content={
              <SpaceBetween size="s">
                <Box><Box fontWeight="bold">Recommended</Box>Business metrics that should exist but don't. Without them, you cannot answer: "Is my service producing correct business outcomes right now?" These detect issues BEFORE customers notice.</Box>
                <Box><Box fontWeight="bold">Nice-to-have</Box>Business insights that improve understanding and planning — helps answer "How is my service performing over time?" but not critical for detecting active issues.</Box>
                <Box><Box fontWeight="bold">Auto-Rollback Gaps</Box>Business metrics that should trigger automatic deployment rollback. Your current rollback monitors only detect "service is DOWN" — these detect "service is UP but producing WRONG business results."</Box>
                <Box variant="h4" padding={{ top: 's' }}>Within Recommended:</Box>
                <Box><Box fontWeight="bold">🚨 Undetectable Business Failures</Box>Service completes the operation successfully (no errors, no exceptions) but the business outcome is WRONG — items in wrong state, data lost, decisions skipped. Nobody knows until FC operators or customers report the impact hours later.</Box>
                <Box><Box fontWeight="bold">📊 Wrong Decisions from Bad Data</Box>A dependency returns bad/incomplete/stale data on a SUCCESSFUL call. The service trusts this data and makes a wrong business decision — wrong work pool, wrong routing, wrong priority. The decision looks valid but is based on garbage input.</Box>
                <Box><Box fontWeight="bold">👁️ Blind Spots</Box>No failure is happening, but the team cannot answer basic business questions: "What % of items are in each state? Is the distribution normal? Are we producing the expected outcomes?" Without these, regressions are invisible until they compound.</Box>
                <Box><Box fontWeight="bold">🔄 Auto-Rollback Gaps</Box>Specific deployment scenarios where your service can break its core business function while ALL existing rollback alarms (SQS depth, latency, faults, host health) stay green. These prove: "The business still works after this deploy."</Box>
              </SpaceBetween>
            }
          >
            <Link variant="info">Info</Link>
          </Popover>
        }>Filter metrics</Header>
      }>
        <SpaceBetween size="m">
          <TextFilter
            filteringText={search}
            onChange={({ detail }) => setSearch(detail.filteringText)}
            filteringPlaceholder="Search metrics..."
            countText={`${metrics.length} match${metrics.length !== 1 ? 'es' : ''}`}
          />
          <SegmentedControl
            selectedId={filter}
            onChange={({ detail }) => { setFilter(detail.selectedId); setSubFilter('all'); setSectionFilter('all'); }}
            options={[
              { id: 'all', text: `All (${counts.total})` },
              { id: 'recommended', text: `Recommended (${counts.recommended})` },
              { id: 'nice-to-have', text: `Nice-to-have (${counts.niceToHave})` },
              { id: 'auto-rollback', text: `🔄 Auto-Rollback (${counts.autoRollback})` },
            ]}
          />
          {filter !== 'auto-rollback' && (
          <SegmentedControl
            selectedId={sectionFilter}
            onChange={({ detail }) => setSectionFilter(detail.selectedId)}
            options={[
              { id: 'all', text: `All sections` },
              { id: 'service', text: `⚙️ Service (${sectionCounts.service})` },
              { id: 'dependency', text: `🔗 Dependency (${sectionCounts.dependency})` },
            ]}
          />
          )}
          {filter === 'recommended' && Object.keys(tagMap).length > 0 && (
          <SegmentedControl
            selectedId={subFilter}
            onChange={({ detail }) => setSubFilter(detail.selectedId)}
            options={[
              { id: 'all', text: `All (${counts.recommended})` },
              ...Object.entries(tagMap).map(([tag, { count, label }]) => ({
                id: tag,
                text: `${label} (${count})`
              }))
            ]}
          />
          )}
          {filter === 'auto-rollback' && (
          <SegmentedControl
            selectedId={subFilter}
            onChange={({ detail }) => setSubFilter(detail.selectedId)}
            options={[
              { id: 'all', text: 'Analysis & Gaps' },
              { id: 'metrics', text: `Recommended Metrics (${counts.autoRollback})` },
            ]}
          />
          )}
        </SpaceBetween>
      </Container>

      {filter === 'auto-rollback' && subFilter === 'all' && data.autoRollbackAnalysis && (
        <Container header={<Header variant="h2">Auto-Rollback Analysis</Header>}>
          <SpaceBetween size="l">
            {data.autoRollbackAnalysis.currentDetection && (
              <ExpandableSection headerText="✅ What current rollback already detects" defaultExpanded={false}>
                <SpaceBetween size="s">
                  <Box>{data.autoRollbackAnalysis.currentDetection.summary}</Box>
                  {data.autoRollbackAnalysis.currentDetection.whatCurrentRollbackDetects?.map((item: any, i: number) => (
                    <Box key={i} padding={{ left: 'l' }}>
                      <Box fontWeight="bold">{item.category}</Box>
                      <Box fontSize="body-s" color="text-status-inactive">{item.monitors} — {item.howItWorks}</Box>
                    </Box>
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}
            {data.autoRollbackAnalysis.currentDetection?.whatCurrentRollbackCannotDetect && (
              <ExpandableSection headerText="❌ What current rollback CANNOT detect" defaultExpanded={true}>
                <SpaceBetween size="xs">
                  {data.autoRollbackAnalysis.currentDetection.whatCurrentRollbackCannotDetect.map((item: string, i: number) => (
                    <Box key={i} color="text-status-error">• {item}</Box>
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}
            {data.autoRollbackAnalysis.whyWeRecommend && (
              <ExpandableSection headerText="💡 Why we recommend these metrics" defaultExpanded={true}>
                <SpaceBetween size="m">
                  <Box fontWeight="bold">{data.autoRollbackAnalysis.whyWeRecommend.theGap}</Box>
                  <Box>{data.autoRollbackAnalysis.whyWeRecommend.deploymentRiskModel}</Box>
                </SpaceBetween>
              </ExpandableSection>
            )}
            {data.autoRollbackAnalysis.rollbackGaps && (
              <ExpandableSection headerText="🔴 Rollback gap scenarios" defaultExpanded={true}>
                <SpaceBetween size="m">
                  {data.autoRollbackAnalysis.rollbackGaps.map((gap: any, i: number) => (
                    <Container key={i} header={<Header variant="h3"><Badge color="red">{gap.severity || 'CRITICAL'}</Badge> {gap.scenario}</Header>}>
                      <SpaceBetween size="s">
                        <div><Box variant="awsui-key-label">What breaks:</Box><Box>{gap.whatBreaks}</Box></div>
                        <div><Box variant="awsui-key-label">Why all alarms stay green:</Box><Box color="text-status-error">{gap.whyCurrentAlarmsWontFire}</Box></div>
                        <div><Box variant="awsui-key-label">How long until detected today:</Box><Box color="text-status-error" fontWeight="bold">{gap.howLongUntilDetected}</Box></div>
                        <div><Box variant="awsui-key-label">Recommended metric:</Box><Box fontWeight="bold">{gap.recommendedMetric}</Box></div>
                        {gap.existingMetricToUse && <div><Box variant="awsui-key-label">Quick fix (no new code):</Box><Box color="text-status-success">{gap.existingMetricToUse}</Box></div>}
                        {gap.needsNewCode && <Box fontSize="body-s" color="text-status-inactive">⚠️ Needs new code instrumentation</Box>}
                      </SpaceBetween>
                    </Container>
                  ))}
                </SpaceBetween>
              </ExpandableSection>
            )}
          </SpaceBetween>
        </Container>
      )}

      {!(filter === 'auto-rollback' && subFilter === 'all') && (
        <>
          <Box color="text-status-inactive">Showing {metrics.length} of {counts.total} metrics</Box>
          {metrics.map((m, i) => (
            <MetricCard key={i} index={i + 1} metric={m} feedback={data.feedback?.[m.name]} onFeedback={onFeedback} />
          ))}
        </>
      )}
    </SpaceBetween>
  );
};

const MetricCard: React.FC<{
  index: number;
  metric: MissingMetric;
  feedback?: string;
  onFeedback: (name: string, val: string) => void;
}> = ({ index, metric: m, feedback, onFeedback }) => (
  <Container
    header={
      <Header
        actions={
          <SpaceBetween size="xs" direction="horizontal">
            <Badge color={m.tag === 'silent-failure' ? 'red' : m.tag === 'deployment-safety' ? 'blue' : m.tag === 'data-quality' ? 'red' : 'grey'}>
              {m.tag === 'silent-failure' ? '🚨 Undetectable Failure' : m.tag === 'data-quality' ? '📊 Wrong Decision' : m.tag === 'business-visibility' ? '👁️ Blind Spot' : '🔄 Deploy Gap'}
            </Badge>
            {m.section && <Badge color="grey">{m.section === 'auto-rollback' ? '🔄 AUTO-ROLLBACK' : m.section === 'service' ? '⚙️ SERVICE' : '🔗 DEPENDENCY'}</Badge>}
            {m.priority === 'AUTO-ROLLBACK-EXISTING' && <Badge color="green">✅ Existing Metric — Just Add Alarm</Badge>}
            {m.section === 'auto-rollback' && m.priority === 'recommended' && <Badge color="grey">⚠️ Needs New Code</Badge>}
            {m.silent === 'yes' && <Badge color="red">SILENT</Badge>}
          </SpaceBetween>
        }
      >
        <Box color="text-status-inactive" display="inline" fontSize="heading-s">#{index}</Box>{' '}
        <code>{m.name}</code>
      </Header>
    }
  >
    <SpaceBetween size="m">
      {m.what && (
        <div>
          <Box variant="awsui-key-label" fontSize="body-s">WHAT IS THIS METRIC?</Box>
          <Box>{m.what}</Box>
        </div>
      )}
      {m.why && (
        <div>
          <Box variant="awsui-key-label" fontSize="body-s">WHY IT MATTERS</Box>
          {m.why.includes('• Impact if not fixed:') ? (
            <>
              <Box>{m.why.split('• Impact if not fixed:')[0].trim()}</Box>
              <Box padding={{ top: 's' }} color="text-status-error" fontWeight="bold">
                • Impact if not fixed: <Box display="inline" color="text-status-error" fontWeight="normal">{m.why.split('• Impact if not fixed:')[1].trim()}</Box>
              </Box>
            </>
          ) : (
            <Box>{m.why}</Box>
          )}
        </div>
      )}
      {m.current && (
        <div>
          <Box variant="awsui-key-label" fontSize="body-s">CURRENT BEHAVIOR</Box>
          <Box color="text-status-error">{m.current}</Box>
        </div>
      )}
      {m.where && (
        <div>
          <Box variant="awsui-key-label" fontSize="body-s">📋 REFERENCE CODE</Box>
          <Box variant="code" fontSize="body-s">{m.where}</Box>
        </div>
      )}

      <ExpandableSection headerText="How to implement">
        <SpaceBetween size="s">
          {m.implement && (
            <div>
              <Box variant="awsui-key-label">🔧 How to implement</Box>
              <Box fontSize="body-s">{m.implement}</Box>
            </div>
          )}
          {m.crLink && (
            <div>
              <Box variant="awsui-key-label">Code Review</Box>
              <Link href={m.crLink} external>{m.crLink}</Link>
            </div>
          )}
        </SpaceBetween>
      </ExpandableSection>

      <Box float="right" padding={{ top: 'xs' }}>
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
          <Box fontSize="heading-s" color="text-status-inactive">🗳️ Helpful?</Box>
          <Badge color={feedback === 'helpful' ? 'green' : 'grey'}>
            <span onClick={() => onFeedback(m.name, 'helpful')} style={{ cursor: 'pointer', fontSize: '18px' }}>👍</span>
          </Badge>
          <Badge color={feedback === 'not_helpful' ? 'red' : 'grey'}>
            <span onClick={() => onFeedback(m.name, 'not_helpful')} style={{ cursor: 'pointer', fontSize: '18px' }}>👎</span>
          </Badge>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  </Container>
);

const StatCard: React.FC<{ value: number; label: string; type: 'error' | 'warning' | 'info' | 'pending' }> = ({ value, label, type }) => (
  <div>
    <Box variant="awsui-key-label">{label}</Box>
    <Box fontSize="display-l" fontWeight="bold">
      <StatusIndicator type={type}>{String(value)}</StatusIndicator>
    </Box>
  </div>
);
